<template>
  <div v-show="visible" class="task-panel-layer" :data-qid="qid">
    <div class="task-panel-scrim" v-if="visible" @click="$emit('close')"></div>
    <!-- Only built while open: every task row has its own panel -->
    <aside v-if="visible"
           class="task-panel"
           :class="{'task-panel--full': fullScreen}"
           role="dialog"
           aria-modal="true"
    >
      <header class="task-panel__head task-panel__head--simple">
        <div class="task-panel__top">
          <div class="task-panel__crumb">
            <template v-for="(part, index) in crumb">
              <font-awesome-icon v-if="index" :key="'crumb-sep-' + index" icon="chevron-right" class="mx-2"/>
              <span :key="'crumb-' + index">{{ part }}</span>
            </template>
          </div>
          <slot name="nav"></slot>
          <span class="task-panel__nav"
                @click="fullScreen = !fullScreen"
                v-b-tooltip.hover
                :title="$t('tasks.v2.full_screen')"
                :data-qid="qid + '-full-screen'"
          ><font-awesome-icon :icon="fullScreen ? 'compress' : 'expand'"/></span>
          <span class="task-panel__nav" @click="$emit('close')" :data-qid="qid + '-close'"><font-awesome-icon icon="times"/></span>
        </div>

        <div class="task-panel__ref" v-if="eyebrow">{{ eyebrow }}</div>
        <div class="task-panel__title">
          <h2 :data-qid="qid + '-title'"><slot name="title">{{ title }}</slot></h2>
          <span v-if="status" class="v2-tag" :class="statusTag.cls">{{ statusTag.text }}</span>
        </div>

        <div class="task-panel__meta">
          <span v-if="users">
            {{ $t('tasks.table.task_assignee') }}
            <span class="task-panel__avatars" v-if="users.length">
              <Avatar v-for="user in users"
                      :key="'panel-user-' + user.id"
                      :object="user"
                      size="xs"
                      v-b-tooltip.hover
                      :title="user.name"
              ></Avatar>
            </span>
            <span v-else class="engagement-unassigned-pill">{{ $t('projects.engagement.filter.unassigned') }}</span>
          </span>
          <span v-if="dueDate" :class="{'text-danger': overdue}">{{ $t('tasks.v2.due') }} <b>{{ dueDate | formatDate('DD MMM', true) }}</b></span>
          <slot name="meta"></slot>
        </div>
      </header>

      <div class="task-panel__body" :class="{'task-panel__body--single': !$slots.rail}">
        <div class="task-panel__main" :data-qid="qid + '-main'">
          <slot></slot>
        </div>
        <div v-if="$slots.rail" class="task-panel__rail" :data-qid="qid + '-rail'">
          <slot name="rail"></slot>
        </div>
      </div>

      <footer v-if="$slots.actions" class="task-panel__actions" :data-qid="qid + '-actions'">
        <span class="task-panel__actions-hint"><slot name="hint"></slot></span>
        <slot name="actions"></slot>
      </footer>
    </aside>
  </div>
</template>

<script>
import Avatar from '@/components/Avatar.vue';
import { v2ModalsOn, v2ModalsOff } from '@/util/v2Modals.js';

// The frame of a task panel in the engagement task panel's design (US 9478): header with the path, title, status,
// assignees and due date; the task on the left, help and related tasks on the right; the actions at the bottom.
// show() and hide() match the b-modal it replaces, so a task component can keep calling $refs['task-modal'].show().
export default {
  name: 'TaskPanelFrame',
  props: {
    title: { type: String, default: '' },
    eyebrow: { type: String, default: '' },
    crumb: { type: Array, default: () => [] },
    status: { type: String, default: '' },
    users: { type: Array, default: null },
    dueDate: { type: String, default: '' },
    qid: { type: String, default: 'task-panel' }
  },
  components: {
    Avatar
  },
  data() {
    return {
      visible: false,
      fullScreen: false
    };
  },
  computed: {
    statusTag() {
      const tags = {
        completed: { cls: 'v2-tag--teal', text: this.$t('system.completed') },
        'in progress': { cls: 'v2-tag--amber', text: this.$t('system.in_progress') },
        pending: { cls: 'v2-tag--grey', text: this.$t('system.pending') }
      };
      return tags[this.status] || tags.pending;
    },
    overdue() {
      const moment = require('moment');
      return this.status !== 'completed' && !!this.dueDate && moment(String(this.dueDate).substring(0, 10)).isBefore(moment().startOf('day'));
    }
  },
  methods: {
    // Dialogs opened from the task (e.g. editing a section) get the new look while the panel is open
    show() {
      if (!this.visible) {
        v2ModalsOn();
      }
      this.visible = true;
      document.body.classList.add('task-panel-open');
    },
    hide() {
      if (this.visible) {
        v2ModalsOff();
      }
      this.visible = false;
      document.body.classList.remove('task-panel-open');
    }
  },
  // Like a b-modal, the panel lives in <body>: some pages mount task components inside hidden elements
  mounted() {
    document.body.appendChild(this.$el);
  },
  beforeDestroy() {
    if (this.visible) {
      document.body.classList.remove('task-panel-open');
      v2ModalsOff();
    }
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  }
}
</script>
